import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { IProfileAgent } from "../../../@types/api-data.types";
import { AgentApi } from "../../../utils/api/Agent";
import {
  getCdnImageUrl,
  getImageUrl,
  getProfileImageUrl,
} from "../../../utils/helpers/common";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { clientRoutes } from "../../../utils/routes";
import Loading from "../../loading/Loading";
import "./AgentMenuDetails.scss";
import { useNavigate } from "react-router-dom";

interface ThemeColor {
  r: number;
  g: number;
  b: number;
  pixel_counts: number;
}

const AgentMenuDetails = ({
  handleClose,
  agentData,
}: {
  handleClose: any;
  agentData: IProfileAgent | null;
}) => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    isLoadingSwitch: false,
  });

  const getProfileImageDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.medium_image_url) {
      return getCdnImageUrl(uploadRecord.medium_image_url);
    }
    if (uploadRecord.small_image_url) {
      return getCdnImageUrl(uploadRecord.small_image_url);
    }
    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }
    if (uploadRecord.compressed_file_path) {
      return getImageUrl(uploadRecord.compressed_file_path);
    }
    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }
    return "";
  };

  const getAgencyLogoDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }
    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }
    if (uploadRecord.compressed_file_path) {
      return getImageUrl(uploadRecord.compressed_file_path);
    }
    return "";
  };

  const getBrandLogoDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }
    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }
    if (uploadRecord.compressed_file_path) {
      return getImageUrl(uploadRecord.compressed_file_path);
    }
    return "";
  };

  const onClickSwitch = async () => {
    let userData: any = LocalStorageHelpers.getAgentData();

    setState({
      ...state,
      isLoadingSwitch: true,
    });

    let formData = new FormData();
    formData.append("switch_to_user_role", "agency");
    formData.append("user_id", userData?.agency_ref?._id || "");
    let res = await AgentApi?.switchUserProfile(formData);

    if (res?.data?.success) {
      toast.custom(
        (t) => (
          <div
            className={`switch-toast ${
              t.visible ? "animate-enter" : "animate-leave"
            }`}
          >
            <span>Switched to agency profile</span>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="switch-toast-close"
            >
              ✕
            </button>
          </div>
        ),
        {
          position: "bottom-right",
          duration: 1000,
        },
      );
      navigate("/");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      toast.error(res?.data?.message || "User not switch please reload!");
    }

    setState({
      ...state,
      isLoadingSwitch: false,
    });
  };

  useEffect(() => {
    const userProfileImages = document.querySelectorAll(".user-profile-image");

    userProfileImages.forEach((profileImage) => {
      profileImage.addEventListener("error", (event: any) => {
        event.src = "images/profile-placeholder-image.png";
      });
    });
  }, []);

  const rgbToCss = (color?: ThemeColor) => {
    if (!color) return "transparent";
    return `rgb(${color.r}, ${color.g}, ${color.b})`;
  };

  return (
    <div className="agent-menu-details-container">
      <div className="header-menu-top-contain-container">
        <div className="header-profile-image">
          {/* <img
            src={getProfileImageUrl(
              agentData?.profile_image_ref?.compressed_file_path || ""
            )}
            alt={agentData?.profile_image_ref?.file_name}
          /> */}
          <img
            src={
              getProfileImageDisplayUrl(agentData?.profile_image_ref) ||
              getProfileImageUrl(
                agentData?.profile_image_ref?.compressed_file_path || "",
              )
            }
            alt={agentData?.profile_image_ref?.file_name}
          />
        </div>
        <div className="header-menu-profile-top-contain">
          <Link to={clientRoutes.agentProfileView}>
            <button className="header-menu-profile-btn" onClick={handleClose}>
              Agent Profile
            </button>
          </Link>

          <p className="header-menu-profile-name">{`${agentData?.principal_agent_first_name} ${agentData?.principal_agent_last_name}`}</p>
          <p className="header-menu-profile-name-email">
            {agentData?.email?.slice(0, 22)}
            {agentData?.email?.length || 0 > 22 ? "..." : ""}
          </p>
        </div>
      </div>
      <div className="header-menu-profile-border"></div>
      {agentData?.is_principal_agent ? (
         <>
          {(() => {
            const agencyRef: any = agentData?.agency_ref;
            const useOfficeLogoRaw = agencyRef?.use_office_logo ?? true;
            const brandData = agencyRef?.brand_id;
            const isIndependent = agencyRef?.agency_type === "independent";

            const shouldUseAgency =
              isIndependent || useOfficeLogoRaw || !brandData;

            const agencyLogoUrl = getAgencyLogoDisplayUrl(
              agencyRef?.profile_image_ref,
            );
            const brandLogoUrl = getBrandLogoDisplayUrl(brandData?.brand_logo);

            const finalLogo = shouldUseAgency
              ? agencyLogoUrl || brandLogoUrl
              : brandLogoUrl || agencyLogoUrl;

            const backgroundColor = shouldUseAgency
              ? agencyRef?.base_color_primary?.hex ||
                (agencyRef?.theme_colors?.[0]
                  ? `rgb(${agencyRef.theme_colors[0].r}, ${agencyRef.theme_colors[0].g}, ${agencyRef.theme_colors[0].b})`
                  : "")
              : brandData?.base_color_primary?.hex ||
                (brandData?.theme_colors_response?.[0]
                  ? `rgb(${brandData.theme_colors_response[0].r}, ${brandData.theme_colors_response[0].g}, ${brandData.theme_colors_response[0].b})`
                  : "");

            return (
              <div className="header-menu-agency-contain-container">
                <div
                  className="header-agency-image"
                  style={{
                    backgroundColor: backgroundColor,
                  }}
                >
                  <img
                    src={
                      finalLogo
                        ? finalLogo
                        : "/images/agency_placeholder_image.png"
                    }
                    alt={agencyRef?.profile_image_ref?.file_name}
                    onError={(e: any) => {
                      e.target.src = "/images/agency_placeholder_image.png";
                    }}
                  />
                </div>

               <p className="header-menu-agency-name">
                  {agentData?.agency_ref?.agency_trading_name}
                </p>
                <p className="header-menu-agency-email">
                  {agentData?.agency_ref?.email}
                </p>

                <p className="header-menu-agency-link" onClick={onClickSwitch}>
                  {state?.isLoadingSwitch ? (
                    <div className="switch-loading">
                      <Loading />
                    </div>
                  ) : (
                    " Switch to Agency Profile"
                  )}
                </p>
              </div>
            );
          })()}
          <div className="header-menu-profile-border border"></div>
        </>
      ) : (
        ""
      )}
    </div>
  );
};

export default AgentMenuDetails;
